import { useIsAuthorized } from '@modules/common/components/CanAccess/hooks';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useIsArtefactEditable } from '@modules/common/hooks';
import { mapToEdgeTimeRange } from '@modules/common/utils';
import {
  getEqlWhereString,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { ViewComfyRounded } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import {
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  DashboardUseCase,
  PermissionOperation,
  PermissionResource,
} from 'generated/graphql';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import { ExpressionType } from 'v2/core/repository/eql/types';
import { useGetUser } from 'v2/core/services/user';
import { EditDashboardDetailsDialogContent } from '../EditDashboardDetailsDialogContent';
import { PinDashboardIcon } from '../PinDashboardIcon';
import { FetchingDashboardUpdatesInfo } from './FetchingDashboardUpdatesInfo';

interface DashboardNameProps {
  name: string;
  isReadOnly?: boolean;
}

export const DashboardName = ({ name, isReadOnly }: DashboardNameProps) => {
  const dashboard = useDashboardStore(({ dashboard }) => dashboard);
  const theme = useTheme();
  const {
    tenant: { id: tenantID },
  } = useGetUser();
  const query = useDashboardStore(({ query }) => query);

  const { openModal } = useMuiModalContext();
  const editToken = dashboard?.editToken;

  const isTemplate = dashboard?.useCase === DashboardUseCase.Template;

  const { eql } = useGetEQLExpression({
    tenantID,
    eqlProps: {
      whereClause: query.whereClause,
      expressionType: ExpressionType.Records,
    },
  });

  const eqlWhereString = getEqlWhereString(eql);

  const canManageTemplates = useIsAuthorized({
    resource: PermissionResource.Template,
    operation: PermissionOperation.Write,
  });

  const isEditable = useIsArtefactEditable({
    editToken: dashboard.editToken,
    status: dashboard.status,
  });

  const openEditDetailsDialog = () => {
    if (!isEditable && dashboard.id) {
      return;
    }
    const editableDashboard = {
      ...dashboard,
      editToken,
      filterClause: {
        timeRange: query.duration
          ? mapToEdgeTimeRange(query.duration)
          : undefined,
        whereClause: eqlWhereString,
      },
    };
    openModal({
      contentComponent: () => (
        <EditDashboardDetailsDialogContent dashboard={editableDashboard} />
      ),
      modalProps: {
        fullWidth: true,
        maxWidth: 'md',
      },
    });
  };

  const shouldShowEditButton = (!isReadOnly && isEditable) || !dashboard.id;

  const isPinnable = dashboard?.id && !isReadOnly;

  const onPinChange = (isPinned: boolean) => {
    trackDashboardEvent({
      eventName: isPinned ? 'Dashboard Pinned' : 'Dashboard Unpinned',
      dashboardLink: window.location.href,
      from: 'Dashboard Page',
    });
  };

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Typography variant="h6">{name}</Typography>
      {canManageTemplates && isTemplate && (
        <Tooltip
          arrow
          title="This Dashboard is set as a template"
          placement="top"
        >
          <ViewComfyRounded color="disabled" />
        </Tooltip>
      )}
      {shouldShowEditButton && (
        <IconButton
          size="small"
          color="primary"
          onClick={openEditDetailsDialog}
          aria-label="Edit dashboard details"
        >
          <EditIcon fontSize="inherit" />
        </IconButton>
      )}
      {isPinnable ? (
        <PinDashboardIcon
          dashboardID={dashboard.id}
          iconColor={theme.palette.primary.main}
          onChange={onPinChange}
        />
      ) : null}
      <FetchingDashboardUpdatesInfo />
    </Stack>
  );
};
